/**
 * Permission checks in the UI (RULES §PERM-2, §UI-6). `/api/me.permissions` holds the
 * concrete keys the server resolved from the user's roles (wildcards already expanded by the
 * same resolver the API uses), so a check is set membership. Never check a role or persona.
 */
import type { Me } from './api/types';
import { useMe } from './me';

export type PermissionKey = `${string}.${string}.${string}`;
/** One key, or several where holding any one is enough. */
export type PermissionCheck = PermissionKey | readonly PermissionKey[];

const sets = new WeakMap<Me, Set<string>>();

function keysOf(me: Me): Set<string> {
  let set = sets.get(me);
  if (!set) {
    set = new Set(me.permissions);
    sets.set(me, set);
  }
  return set;
}

export function can(me: Me | null | undefined, check: PermissionCheck | undefined): boolean {
  if (!check) return true;
  if (!me) return false;
  const keys = keysOf(me);
  return typeof check === 'string' ? keys.has(check) : check.some((k) => keys.has(k));
}

export function useCan(check: PermissionCheck | undefined): boolean {
  return can(useMe(), check);
}
